/**
 * Registry de PROVEEDORES DE CLASES EN VIVO (SOLID, Diseño B).
 *
 * Multi-provider: Jitsi embebido / Zoom / Meet / enlace custom. Cada proveedor
 * se auto-describe con un `MeetingProviderManifest` (capacidades + esquema de
 * configuración) y sabe construir su `JoinDescriptor` (embed o external) a partir
 * de la sesión + la config resuelta por alcance + el usuario.
 *
 * El registry es la MISMA pieza de extensibilidad que `learning-blocks/registry`:
 * añadir un proveedor = 1 archivo + `register()`; nada del resto de la app cambia.
 *
 * `meeting_config` es SERVER-ONLY (nunca en sync ni en DTOs de cliente): estas
 * piezas son PURAS y reciben la config ya resuelta como argumento. La ventana
 * horaria y la autorización de `/join` viven en el servidor (F3-G).
 */

import type { FeatureManifest } from "../academy/country/packs";

/** Dominio Jitsi por defecto (espejo de `DEFAULT_JITSI_DOMAIN` en Rust config). */
export const DEFAULT_JITSI_DOMAIN = "meet.jit.si";

/**
 * Un campo del esquema de configuración de un proveedor. La UI genérica de
 * ajustes se genera a partir de `configSchema: SettingsField[]`. Los campos
 * `secret` (x-secret) se renderizan como password y son server-only.
 */
export interface SettingsField {
  /** Clave estable del campo dentro de la config. */
  key: string;
  /** Etiqueta legible (i18n key o texto). */
  label: string;
  /** Tipo de control. */
  type: "text" | "url" | "password" | "boolean" | "number" | "select";
  /** ¿Obligatorio para que la config sea válida? */
  required?: boolean;
  /** Placeholder / ejemplo. */
  placeholder?: string;
  /** Ayuda contextual. */
  help?: string;
  /** Opciones (solo `type: "select"`). */
  options?: readonly { value: string; label: string }[];
  /** Secreto sellado server-only (GET devuelve `{set:true}`). */
  secret?: boolean;
  /** Valor por defecto. */
  default?: string | number | boolean;
}

/** Capacidades declaradas de un proveedor (alimentan el catálogo y la UI). */
export interface MeetingProviderCapabilities {
  /** ¿Se puede embeber en un iframe dentro de la app? (Jitsi sí). */
  embeddable: boolean;
  /** ¿Notifica asistencia vía callback/webhook? */
  attendanceCallback: boolean;
  /** ¿Permite recuperar la grabación automáticamente? */
  recordingFetch: boolean;
  /** ¿Requiere credenciales/config (API key, etc.) para funcionar? */
  requiresCredentials: boolean;
}

/** Manifest auto-descriptivo de un proveedor de clases en vivo. */
export interface MeetingProviderManifest {
  /** Id estable del proveedor (p.ej. "jitsi"). Namespace de catálogo: `media.provider.<id>`. */
  id: string;
  /** Nombre legible. */
  name: string;
  /** Descripción de una línea. */
  description: string;
  /** Capacidades declaradas. */
  capabilities: MeetingProviderCapabilities;
  /** Esquema de configuración (UI genérica). */
  configSchema: readonly SettingsField[];
}

/** Sesión en vivo (subconjunto mínimo que un proveedor necesita para el join). */
export interface LiveSession {
  /** Id de la `class_session`. */
  id: string;
  /** Título de la sesión (para mostrar en el cliente embebido). */
  title?: string | null;
  /** Enlace de reunión (proveedores externos: Zoom/Meet/custom). */
  meetingUrl?: string | null;
  /**
   * Salt provisto por el SERVIDOR para derivar el nombre de sala determinista
   * (Jitsi). Nunca se genera en cliente: garantiza que profesor y alumnos caen
   * en la misma sala sin exponer un secreto adivinable.
   */
  roomSalt?: string | null;
}

/** Usuario que se une (para displayName / rol). */
export interface JoinUser {
  id: string;
  name?: string | null;
  email?: string | null;
  /** Rol en la sesión (p.ej. "teacher" | "student") para moderación. */
  role?: string | null;
}

/** Descriptor de cómo unirse: embebido (iframe) o externo (abrir enlace). */
export type JoinDescriptor =
  | {
      mode: "embed";
      provider: string;
      /** Dominio del servidor (Jitsi). */
      domain: string;
      /** Nombre de sala determinista. */
      roomName: string;
      /** Nombre a mostrar del participante. */
      displayName?: string;
    }
  | {
      mode: "external";
      provider: string;
      /** URL https validada a abrir en pestaña/app externa. */
      url: string;
    };

/**
 * Un proveedor de clases en vivo enchufable. `buildJoin` es PURO (config
 * inyectada) para poder testarse sin red. `Embed` es la carga perezosa del
 * componente de iframe (solo proveedores `embeddable`).
 */
export interface MeetingProvider {
  readonly manifest: MeetingProviderManifest;
  /** Construye el descriptor de unión. Puro y determinista. */
  buildJoin(session: LiveSession, cfg: Record<string, unknown>, user: JoinUser): JoinDescriptor;
  /** Carga perezosa del componente embebido (opcional). */
  Embed?: () => Promise<unknown>;
}

/**
 * Registro de proveedores de clases en vivo. Espeja `BlockRegistry`:
 * `register`/`get`/`types`, y lanza ante clave duplicada (no pisa proveedores).
 */
export class MeetingProviderRegistry {
  private readonly providers = new Map<string, MeetingProvider>();

  /** Registra un proveedor. Lanza si el `id` ya existe. */
  register(provider: MeetingProvider): this {
    const { id } = provider.manifest;
    if (this.providers.has(id)) {
      throw new Error(`MeetingProviderRegistry: proveedor duplicado "${id}"`);
    }
    this.providers.set(id, provider);
    return this;
  }

  /** Registra varios de una vez. */
  registerAll(providers: readonly MeetingProvider[]): this {
    for (const p of providers) {
      this.register(p);
    }
    return this;
  }

  get(id: string): MeetingProvider | undefined {
    return this.providers.get(id);
  }

  has(id: string): boolean {
    return this.providers.has(id);
  }

  /** Ids de proveedores registrados (para el selector de ajustes). */
  types(): string[] {
    return [...this.providers.keys()];
  }

  /** Todos los proveedores registrados. */
  all(): MeetingProvider[] {
    return [...this.providers.values()];
  }
}

/** Clave de catálogo de un proveedor: `media.provider.<id>`. */
export function meetingProviderKey(id: string): string {
  return `media.provider.${id}`;
}

/** Deriva el `FeatureManifest` (forma del catálogo) de un manifest de proveedor. */
export function meetingProviderToFeatureManifest(m: MeetingProviderManifest): FeatureManifest {
  return {
    key: meetingProviderKey(m.id),
    name: m.name,
    description: m.description,
    scope: "org",
    status: "stable",
    // FLAGS: el join de una clase en vivo gatea el proveedor RESUELTO
    // (gate dinámico por prefijo `media.provider.` en handlers/live.rs).
    gateable: true,
    since: "2026-06",
    docsSlug: "profesor",
    implications: {
      navHidden: [`«${m.name}» en los selectores de proveedor (curso/cohorte/sesión)`],
      flowsBlocked: ["Unirse a clases en vivo que resuelvan a este proveedor"],
      dataPolicy: "preserved",
    },
  };
}

// ─── Helpers de proveedores ───────────────────────────────────────────────────

/**
 * Hash determinista (FNV-1a de 32 bits, hex) de `${sessionId}:${salt}`. PURO y
 * testeable. NO es criptográfico: solo hace el nombre de sala no-adivinable a
 * partir del salt server-side (que sí es secreto).
 */
export function roomHash(sessionId: string, salt: string): string {
  const input = `${sessionId}:${salt}`;
  let hash = 0x811c9dc5; // offset basis FNV-1a 32-bit
  for (let i = 0; i < input.length; i++) {
    hash ^= input.codePointAt(i) ?? 0;
    // FNV prime 16777619, en aritmética de 32 bits (Math.imul evita overflow).
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  // >>> 0 → entero sin signo de 32 bits; padded a 8 hex.
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Nombre de sala Jitsi DETERMINISTA: `spaceacademy-{sessionId}-{hash}`. El
 * `salt` lo provee el servidor (secreto): dos clientes con el mismo salt caen en
 * la misma sala; sin el salt no se puede adivinar. Puro (testeable).
 */
export function jitsiRoomName(sessionId: string, salt: string): string {
  return `spaceacademy-${sessionId}-${roomHash(sessionId, salt)}`;
}

/**
 * Valida y normaliza una URL https. Lanza `Error` si no es http(s) válido.
 * Los proveedores externos (Zoom/Meet/custom) exigen https.
 */
export function validateHttpsUrl(raw: string | null | undefined): string {
  if (!raw || typeof raw !== "string") {
    throw new Error("meeting: URL de reunión requerida");
  }
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    throw new Error(`meeting: URL inválida "${raw}"`);
  }
  if (url.protocol !== "https:") {
    throw new Error(`meeting: la URL debe ser https (fue "${url.protocol}")`);
  }
  return url.toString();
}
